/**
 * HeaderActivityPopover Container Component
 *
 * Renders the bell icon trigger and activity feed popover in the header.
 * Delegates notification data and mark-as-read logic to the shared
 * useActivityNotifications hook so the same logic can be reused by SidePanel.
 * @module components/HeaderActivityPopover/HeaderActivityPopoverContainer
 */

import { Bell } from "lucide-react-native";
import React, { useCallback, useMemo, useState } from "react";
import { useWindowDimensions } from "react-native";

import { Box } from "@/components/atoms/Box";
import { Heading } from "@/components/atoms/Heading";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { useBreakpointValue } from "@/components/atoms/useBreakpointValue";
import HeaderActivityPopoverView from "./HeaderActivityPopoverView";

import useActivityNotifications from "@/hooks/useActivityNotifications";
import useMobileScreen from "@/hooks/useMobileScreen";
import { useTheme } from "@/providers/ThemeProvider";

/** Props for HeaderActivityPopoverContainer */
interface HeaderActivityPopoverContainerProps {
  /** Optional custom trigger renderer. When provided, overrides the default bell button. */
  readonly renderTrigger?: (triggerProps: object) => React.JSX.Element;
}

/**
 * Container component for the activity notifications popover in the header.
 * Handles responsive width calculation and popover open/close state while
 * delegating notification data to the shared useActivityNotifications hook.
 * @param root0 - Component props
 * @param root0.renderTrigger - Optional custom trigger renderer, overrides default bell button
 */
const HeaderActivityPopoverContainer = ({
  renderTrigger: externalTrigger,
}: HeaderActivityPopoverContainerProps = {}) => {
  const { width } = useWindowDimensions();
  const [isActivityVisible, setIsActivityVisible] = useState(false);
  const isSmallScreen = useMobileScreen();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const { unreadActivityCount, handleMarkAllActivity } =
    useActivityNotifications();

  const handleOpenActivityPopover = useCallback(() => {
    setIsActivityVisible(true);
  }, []);
  const handleChoseActivityPopover = useCallback(() => {
    setIsActivityVisible(false);
  }, []);

  const responsiveWidth = useBreakpointValue({
    default: width * 0.9,
    md: 640,
    lg: 540,
  });

  const popoverWidth = useMemo(() => {
    if (isSmallScreen) {
      return Math.min(responsiveWidth, width - 32);
    }

    const crossOffsetAdjustment = 60;
    const rightMargin = 24;
    const availableWidth = width - crossOffsetAdjustment - rightMargin;

    return Math.min(540, Math.max(320, availableWidth));
  }, [isSmallScreen, responsiveWidth, width]);

  const renderHeaderComponent = useCallback(() => {
    return (
      <Stack
        direction="horizontal"
        className="items-center justify-between pb-6 pt-4"
      >
        <Heading variant="title">Recent Activity</Heading>

        <Pressable onPress={handleMarkAllActivity}>
          <Text variant="unstyled" className="text-content-primary">
            Mark all as read
          </Text>
        </Pressable>
      </Stack>
    );
  }, [handleMarkAllActivity]);

  const renderTrigger = useCallback(
    (triggerProps: object) => (
      <Pressable
        {...triggerProps}
        className="rounded-none px-3 py-2"
        accessible
        accessibilityLabel={
          unreadActivityCount > 0
            ? `View activity notifications. ${unreadActivityCount} unread`
            : "View activity notifications"
        }
        accessibilityRole="button"
      >
        {unreadActivityCount > 0 ? (
          <Box className="relative flex items-center justify-center">
            <Icon as={Bell} size="lg" />
            <Box className="absolute right-0 top-0 rounded-full bg-accent-primary p-1" />
          </Box>
        ) : (
          <Icon as={Bell} size="lg" />
        )}
      </Pressable>
    ),
    [unreadActivityCount]
  );

  return (
    <HeaderActivityPopoverView
      isOpen={isActivityVisible}
      widthContent={popoverWidth}
      onOpen={handleOpenActivityPopover}
      onClose={handleChoseActivityPopover}
      renderHeaderComponent={renderHeaderComponent}
      renderTrigger={externalTrigger ?? renderTrigger}
      isDark={isDark}
    />
  );
};

export default HeaderActivityPopoverContainer;
